import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageBody from '../components/ui/PageBody';
import SearchField from '../components/SearchField';
import IconButton from '../components/ui/IconButton';
import {
    getAllTags,
    updateTag,
    deleteTag,
} from '../api';

const getTagId = (tag) => Number(tag.id ?? tag.tag_id);

const getTagName = (tag) =>
    tag.name ?? tag.title ?? tag.tag_name ?? '';

export default function TagsContent() {
    const { t } = useTranslation();

    const [tags, setTags] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadTags = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getAllTags();

            const items = Array.isArray(response)
                ? response
                : Array.isArray(response?.items)
                    ? response.items
                    : [];

            setTags(items);

            setSelectedIds((current) =>
                current.filter((id) =>
                    items.some((tag) => getTagId(tag) === id)
                )
            );
        } catch (error) {
            console.error('Failed to load tags:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTags();
    }, [loadTags]);

    const filteredTags = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return tags;
        }

        return tags.filter((tag) =>
            getTagName(tag).toLowerCase().includes(query)
        );
    }, [tags, search]);

    const visibleIds = filteredTags
        .map(getTagId)
        .filter(Number.isFinite);

    const allSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => selectedIds.includes(id));

    const toggleTag = (id) => {
        setSelectedIds((current) =>
            current.includes(id)
                ? current.filter((selectedId) => selectedId !== id)
                : [...current, id]
        );
    };

    const toggleSelectAll = () => {
        setSelectedIds((current) =>
            allSelected
                ? current.filter((id) => !visibleIds.includes(id))
                : [...new Set([...current, ...visibleIds])]
        );
    };

    const handleRename = async (tag) => {
        const id = getTagId(tag);
        const currentName = getTagName(tag);

        const newName = window.prompt(
            t('tags.enterNewName', 'Enter a new tag name'),
            currentName
        );

        if (newName === null) {
            return;
        }

        const name = newName.trim();

        if (!name) {
            window.alert(
                t('tags.emptyName', 'Tag name cannot be empty')
            );
            return;
        }

        if (name.length > 100) {
            window.alert(
                t(
                    'tags.nameTooLong',
                    'Tag name must not exceed 100 characters'
                )
            );
            return;
        }

        if (name === currentName) {
            return;
        }

        setBusy(true);

        try {
            await updateTag(id, { name });
            await loadTags();
        } catch (error) {
            console.error('Failed to rename tag:', error);

            window.alert(
                t('tags.renameError', 'Failed to rename tag')
            );
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (ids = selectedIds) => {
        const uniqueIds = [...new Set(ids)].filter(Number.isFinite);

        if (uniqueIds.length === 0) {
            return;
        }

        const confirmed = window.confirm(
            t(
                'tags.confirmDelete',
                'Are you sure you want to delete the selected tags?'
            )
        );

        if (!confirmed) {
            return;
        }

        setBusy(true);

        try {
            for (const id of uniqueIds) {
                await deleteTag(id);
            }

            setSelectedIds((current) =>
                current.filter((id) => !uniqueIds.includes(id))
            );

            await loadTags();
        } catch (error) {
            console.error('Failed to delete tags:', error);

            window.alert(
                t('tags.deleteError', 'Failed to delete tags')
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="tags-management">
            <div className="tags-management-toolbar">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder={t('tags.search', 'Search tags')}
                />
            </div>

            <div className="archived-articles-list-wrapper">
                <div className="archived-article-actions">
                    <div className="tag-select-all">
                        <span>
                            {t('archivedArticles.selectAll', 'Select all')}
                        </span>

                        <div className="archived-article-select-all">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                                disabled={visibleIds.length === 0 || busy}
                                aria-label={t(
                                    'archivedArticles.selectAll',
                                    'Select all'
                                )}
                            />
                        </div>

                        <IconButton
                            className="archived-article-delete"
                            icon="fa-regular fa-trash-can"
                            label={t('tags.delete', 'Delete selected')}
                            onClick={() => handleDelete()}
                            disabled={busy || selectedIds.length === 0}
                        />
                    </div>
                </div>

                <PageBody className="tags-list">
                    {loading ? (
                        <p>
                            {t('common.loading', 'Loading...')}
                        </p>
                    ) : filteredTags.length === 0 ? (
                        <p>
                            {t('tags.noTags', 'No tags found')}
                        </p>
                    ) : (
                        filteredTags.map((tag) => {
                            const id = getTagId(tag);

                            return (
                                <article
                                    key={id}
                                    className="article-list-item"
                                >
                                    <div className="article-list-content">
                                        <span className="tag-management-name">
                                            <i
                                                className="fa-solid fa-hashtag tag-management-icon"
                                                aria-hidden="true"
                                            />
                                            {getTagName(tag)}
                                        </span>
                                    </div>


                                    <div className="tag-management-actions">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(id)}
                                            onChange={() => toggleTag(id)}
                                            disabled={busy}
                                            aria-label={t(
                                                'tags.selectTag',
                                                'Select tag'
                                            )}
                                        />

                                        <IconButton
                                            icon="fa-regular fa-pen-to-square"
                                            label={t(
                                                'tags.edit',
                                                'Edit tag'
                                            )}
                                            onClick={() => handleRename(tag)}
                                            disabled={busy}
                                            className="icon-button--plain"
                                        />

                                        <IconButton
                                            className="archived-article-delete"
                                            icon="fa-regular fa-trash-can"
                                            label={t(
                                                'tags.delete',
                                                'Delete tag'
                                            )}
                                            onClick={() => handleDelete([id])}
                                            disabled={busy || !selectedIds.includes(id)}
                                        />
                                    </div>
                                </article>
                            );
                        })
                    )}
                </PageBody>
            </div>
        </div>
    );
}
